import React, { useState, useCallback, useEffect, ReactElement } from "react";

import { createIdleRequest } from "@/components/AppProvider/AppNotificationProvider/useBulkActionToast";
import { GridTableSelectionBar } from "@/components/GridTable/helpers/GridTableSelectionBar";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { MessagesTooltip } from "@/components/MessagesTooltip";
import { FrontendAddonRaw } from "@/domain/FrontendAddon";
import {
  Button,
  MenuItem,
  MenuList,
  ToolbarDivider,
} from "@fluentui/react-components";
import {
  Checkmark20Regular,
  Delete20Regular,
  SubtractCircle20Regular,
} from "@fluentui/react-icons";
import { useAppAccount } from "../../../AppProvider/AppAccountProvider";
import { DialogPhaseType } from "../../helpers/hooks";
import { DialogFrontendAddonDelete } from "./DialogFrontendAddonDelete";
import { DialogFrontendAddonRemove } from "./DialogFrontendAddonRemove";
import { DialogFrontendAddonRemoveSome } from "./DialogFrontendAddonRemoveSome";
import {
  FrontendAddonEnableRequest,
  useFrontendAddonEnableAction,
} from "./useFrontendAddonEnableAction";

/**
 * @description 複数の場所で利用されるcontextItemsとダイアログを生成
 */
export const useDialogContextMenuItemsFrontendAddon = (
  runReloadFrontendAddons: () => Promise<void>,
  returnPath: string
) => {
  const [selectedFrontendAddonIds, setSelectedFrontendAddonIds] = useState(
    [] as string[]
  );
  const { isAADeveloperOrAbove } = useAppAccount();
  const { hasPermission } = usePermissions();
  const hasWritePermission =
    isAADeveloperOrAbove || hasPermission("frontend_addons_owned", "write");
  const hasDeletePermission =
    isAADeveloperOrAbove || hasPermission("frontend_addons_owned", "delete");

  const [deleteDialogPhase, setDeleteDialogPhase] =
    useState<DialogPhaseType>(null);
  const [removeDialogPhase, setRemoveDialogPhase] =
    useState<DialogPhaseType>(null);
  const [deleteSomeDialogPhase, setDeleteSomeDialogPhase] =
    useState<DialogPhaseType>(null);
  const [isRemoveSomeDialogOpen, setIsRemoveSomeDialogOpen] = useState(false);
  const [enableRequest, setEnableRequest] =
    useState<FrontendAddonEnableRequest>(createIdleRequest);

  const isEnableInProgress = enableRequest.phase !== "idle";

  // 削除の行メニューが借りた選択状態は ESC や背景クリックで閉じると onClickCancel を通らず残るため、ダイアログが閉じたら戻す
  useEffect(() => {
    if (removeDialogPhase === null) {
      setSelectedFrontendAddonIds([]);
    }
  }, [removeDialogPhase]);

  useFrontendAddonEnableAction({
    enableRequest,
    setEnableRequest,
    setSelectedFrontendAddonIds,
    runReloadFrontendAddons,
  });

  const requestEnable = useCallback(
    (itemIds: string[], isSelectionBarAction = false) => {
      setEnableRequest((current) =>
        current.phase === "idle"
          ? { phase: "requested", itemIds, isSelectionBarAction }
          : current
      );
    },
    []
  );

  const getContextMenuItemsEnableFrontendAddon = useCallback(
    (frontendAddon: FrontendAddonRaw): ReactElement => {
      return (
        <MenuList>
          <MenuItem
            key="i1"
            onClick={() => {
              setSelectedFrontendAddonIds([frontendAddon.id]);
              setDeleteDialogPhase("confirm");
            }}
            disabled={
              !hasWritePermission ||
              frontendAddon.is_disabled ||
              isEnableInProgress
            }
          >
            無効化
          </MenuItem>
        </MenuList>
      );
    },
    [hasWritePermission, isEnableInProgress]
  );

  const getContextMenuItemsDisableFrontendAddon = useCallback(
    (frontendAddon: FrontendAddonRaw): ReactElement => {
      return (
        <MenuList>
          <MenuItem
            key="i1"
            onClick={() => requestEnable([frontendAddon.id])}
            disabled={
              !hasWritePermission ||
              !frontendAddon.is_disabled ||
              isEnableInProgress
            }
          >
            有効化
          </MenuItem>
          <MenuItem
            key="i2"
            onClick={() => {
              setSelectedFrontendAddonIds([frontendAddon.id]);
              setRemoveDialogPhase("confirm");
            }}
            disabled={!hasDeletePermission || !frontendAddon.is_disabled}
            style={{
              color:
                !hasDeletePermission || !frontendAddon.is_disabled
                  ? "var(--figma-color-gray60)"
                  : "var(--figma-color-execution-red)",
            }}
          >
            削除
          </MenuItem>
        </MenuList>
      );
    },
    [hasWritePermission, hasDeletePermission, isEnableInProgress, requestEnable]
  );

  const renderDialogsFrontendAddon = useCallback(
    (frontendAddons: FrontendAddonRaw[]) => {
      return (
        <>
          <DialogFrontendAddonDelete
            runReloadFrontendAddons={runReloadFrontendAddons}
            frontendAddons={frontendAddons}
            selectedFrontendAddonIds={selectedFrontendAddonIds}
            setSelectedFrontendAddonIds={setSelectedFrontendAddonIds}
            dialogPhase={deleteDialogPhase}
            setDialogPhase={setDeleteDialogPhase}
          />
          <DialogFrontendAddonRemove
            runReloadFrontendAddons={runReloadFrontendAddons}
            frontendAddons={frontendAddons}
            selectedFrontendAddonIds={selectedFrontendAddonIds}
            setSelectedFrontendAddonIds={setSelectedFrontendAddonIds}
            dialogPhase={removeDialogPhase}
            setDialogPhase={setRemoveDialogPhase}
            returnPath={returnPath}
          />
          <DialogFrontendAddonDelete
            runReloadFrontendAddons={runReloadFrontendAddons}
            frontendAddons={frontendAddons}
            selectedFrontendAddonIds={selectedFrontendAddonIds}
            setSelectedFrontendAddonIds={setSelectedFrontendAddonIds}
            dialogPhase={deleteSomeDialogPhase}
            setDialogPhase={setDeleteSomeDialogPhase}
            preserveSelectionOnCancel
          />
          <DialogFrontendAddonRemoveSome
            runReloadFrontendAddons={runReloadFrontendAddons}
            frontendAddons={frontendAddons}
            selectedFrontendAddonIds={selectedFrontendAddonIds}
            setSelectedFrontendAddonIds={setSelectedFrontendAddonIds}
            returnPath={returnPath}
            open={isRemoveSomeDialogOpen}
            setOpen={setIsRemoveSomeDialogOpen}
          />
        </>
      );
    },
    [
      selectedFrontendAddonIds,
      deleteDialogPhase,
      removeDialogPhase,
      deleteSomeDialogPhase,
      isRemoveSomeDialogOpen,
      runReloadFrontendAddons,
    ]
  );

  const renderFrontendAddonsSelectionBar = useCallback(
    (frontendAddons: FrontendAddonRaw[]) => {
      const selectedFrontendAddons = frontendAddons.filter((frontendAddon) =>
        selectedFrontendAddonIds.includes(frontendAddon.id)
      );
      if (selectedFrontendAddons.length === 0) return null;

      const hasEnabledSelection = selectedFrontendAddons.some(
        (frontendAddon) => !frontendAddon.is_disabled
      );
      const hasDisabledSelection = selectedFrontendAddons.some(
        (frontendAddon) => frontendAddon.is_disabled
      );
      const isDeleteAvailable = !hasEnabledSelection && hasDeletePermission;

      const enableErrorMessages = [
        ...(!hasWritePermission ? ["アドオンを編集する権限がありません"] : []),
        ...(isEnableInProgress ? ["有効化の処理中です"] : []),
      ];
      const disableErrorMessages = [
        ...(!hasWritePermission ? ["アドオンを編集する権限がありません"] : []),
        ...(isEnableInProgress ? ["有効化の処理中です"] : []),
      ];
      const removeErrorMessages = [
        ...(hasEnabledSelection
          ? ["選択に有効なアドオンが含まれています"]
          : []),
        ...(!hasDeletePermission ? ["アドオンを削除する権限がありません"] : []),
      ];

      return (
        <GridTableSelectionBar
          clearLabel={`${selectedFrontendAddons.length}件を選択解除`}
          onClear={() => setSelectedFrontendAddonIds([])}
          ariaLabel="選択中のフロントエンドアドオンの操作"
        >
          {hasDisabledSelection && (
            <>
              <ToolbarDivider />
              <MessagesTooltip messages={enableErrorMessages}>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Checkmark20Regular />}
                  onClick={() => {
                    requestEnable(
                      selectedFrontendAddons
                        .filter((frontendAddon) => frontendAddon.is_disabled)
                        .map((frontendAddon) => frontendAddon.id),
                      true
                    );
                  }}
                  disabled={enableErrorMessages.length > 0}
                >
                  有効化
                </Button>
              </MessagesTooltip>
            </>
          )}
          {hasEnabledSelection && (
            <>
              <ToolbarDivider />
              <MessagesTooltip messages={disableErrorMessages}>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<SubtractCircle20Regular />}
                  onClick={() => {
                    setDeleteSomeDialogPhase("confirm");
                  }}
                  disabled={disableErrorMessages.length > 0}
                >
                  無効化
                </Button>
              </MessagesTooltip>
            </>
          )}
          {hasDisabledSelection && (
            <>
              <ToolbarDivider />
              <MessagesTooltip messages={removeErrorMessages}>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Delete20Regular />}
                  style={{
                    color: isDeleteAvailable
                      ? "var(--figma-color-execution-red)"
                      : "var(--figma-color-gray60)",
                  }}
                  onClick={() => {
                    setIsRemoveSomeDialogOpen(true);
                  }}
                  disabled={removeErrorMessages.length > 0}
                >
                  削除
                </Button>
              </MessagesTooltip>
            </>
          )}
        </GridTableSelectionBar>
      );
    },
    [
      selectedFrontendAddonIds,
      hasWritePermission,
      hasDeletePermission,
      isEnableInProgress,
      requestEnable,
    ]
  );

  return {
    renderDialogsFrontendAddon,
    getContextMenuItemsEnableFrontendAddon,
    getContextMenuItemsDisableFrontendAddon,
    selectedFrontendAddonIds,
    setSelectedFrontendAddonIds,
    renderFrontendAddonsSelectionBar,
  };
};
